import dayjs from "dayjs";
import React, { useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { Suggestion } from "./Suggestion";
import { OrgRecommendationData } from "../../../models/OrgRecommendationData";
import { OptionalRender } from "../../Common/OptionalRender";

import "./styles.scss";

export interface ISuggestionsProps {
  buttonType: "link" | "button";
  shownAll: boolean;
  minimumSize?: number;
  isDashboard?: boolean;
  organization_id?: string;
  recommendations: OrgRecommendationData[] | undefined;
}

export const Suggestions: React.FunctionComponent<ISuggestionsProps> = (
  props
) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityDetailsPage.suggestions.${key}`);

  const [shownAll, setShownAll] = useState<boolean>(props.shownAll); // false

  const {
    buttonType,
    minimumSize,
    isDashboard,
    organization_id,
    recommendations,
  } = props;

  const minSize = minimumSize !== undefined ? minimumSize : 2;

  const sanitizedRecommendations: OrgRecommendationData[] = [];

  recommendations?.forEach((recommendation) => {
    if (
      sanitizedRecommendations.findIndex(
        (item) => item.title === recommendation.title
      ) === -1
    ) {
      sanitizedRecommendations.push(recommendation);
    }
  });

  const date =
    sanitizedRecommendations.length > 0
      ? dayjs(sanitizedRecommendations[0].generated_date).format("YYYY-MM-DD")
      : undefined;

  return (
    <div className="white-border-panel suggestions-module padding32">
      <div className="titles">
        {t(isDashboard ? "dashboard_suggestions" : "suggestions")}
        <div className="rights">
          <small>
            {t("generated_by_ai")}
            <OptionalRender mustBeImpersonating={true}>
              {date ? `　${date}` : ""}
            </OptionalRender>
          </small>
        </div>
      </div>
      {!recommendations ? (
        <div className="row loading-row">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <ul className="suggestion-list">
            {sanitizedRecommendations.length > 0 ? (
              sanitizedRecommendations
                .slice(0, shownAll ? sanitizedRecommendations.length : minSize)
                .map((data: OrgRecommendationData, index: number) => (
                  <li key={index}>
                    <Suggestion data={data} />
                  </li>
                ))
            ) : (
              <li className="no-suggestion">
                <div className="txt">
                  <div>{t("no_suggestion")}</div>
                </div>
              </li>
            )}
          </ul>
          <div className="show-all">
            {buttonType === "link" &&
              sanitizedRecommendations.length > minSize && (
                <button
                  className={`btn ${shownAll ? "btn-hide" : "btn-show"}`}
                  onClick={() => {
                    setShownAll(!shownAll);
                  }}
                >
                  {t(shownAll ? "show_less" : "show_all")}
                  <i className="icons icon-drop"></i>
                </button>
              )}
            {((!shownAll &&
              buttonType === "button" &&
              sanitizedRecommendations.length > minSize) ||
              isDashboard) && (
              <NavLink
                to={`/recommendations/organization/${organization_id}`}
                className="btn btn-blue"
                onClick={() => {
                  setShownAll(true);
                }}
              >
                {t("see_all_suggestions")}
              </NavLink>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Suggestions;
